import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Set up axios defaults
  axios.defaults.baseURL = process.env.REACT_APP_API_URL || window.location.origin;
  
  // Add token to axios headers if it exists
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Load user data if token exists
  useEffect(() => {
    const loadUser = async () => {
      if (!token) {
        setLoading(false);
        setIsAuthenticated(false);
        return;
      }

      try {
        const res = await axios.get('/api/auth/me');
        setUser(res.data.user);
        setIsAuthenticated(true);
        setLoading(false);
      } catch (err) {
        console.error('Error loading user:', err);
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
        setError('Sesión expirada. Por favor inicie sesión nuevamente.');
        setLoading(false);
      }
    };

    loadUser();
  }, [token]);

  // Register user
  const register = async (userData) => {
    try {
      setLoading(true);
      const res = await axios.post('/api/auth/register', userData);
      
      // Store email for verification page
      localStorage.setItem('pendingVerificationEmail', userData.email);
      
      // Return success with needsVerification flag
      return { success: true, needsVerification: true };
    } catch (err) {
      setError(err.response?.data?.error || 'Registro fallido');
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  // Login user
  const login = async (email, password) => {
    try {
      setLoading(true);
      console.log('AuthContext: Attempting login with email:', email);
      const res = await axios.post('/api/auth/login', { email, password });
      console.log('AuthContext: Login response:', res.data);
      
      // Check if user is verified
      if (res.data.user && !res.data.user.is_verified) {
        console.log('AuthContext: User not verified from success response');
        // Store email for verification page
        localStorage.setItem('pendingVerificationEmail', email);
        // No establecer error, solo devolver la bandera needsVerification
        return { needsVerification: true, success: false };
      }
      
      localStorage.setItem('token', res.data.token);
      setToken(res.data.token);
      setUser(res.data.user);
      setIsAuthenticated(true);
      return { success: true };
    } catch (err) {
      console.log('AuthContext: Login error:', err.response?.data);
      // Verificar si el error es porque el usuario no está verificado
      if (err.response?.data?.needsVerification) {
        console.log('AuthContext: User needs verification from error response');
        // Store email for verification page
        localStorage.setItem('pendingVerificationEmail', err.response.data.email || email);
        clearError(); // No mostrar error ya que estamos manejando este caso específico
        return { needsVerification: true, success: false };
      }
      
      setError(err.response?.data?.error || 'Credenciales inválidas');
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  // Logout user
  const logout = async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  // Clear error
  const clearError = () => setError(null);
  
  // Verify email with OTP code
  const verifyEmail = async (email, code) => {
    try {
      setLoading(true);
      const res = await axios.post('/api/auth/verify-code', { email, code });
      return { success: true, user: res.data.user };
    } catch (err) {
      setError(err.response?.data?.error || 'Verificación fallida');
      return { success: false };
    } finally {
      setLoading(false);
    }
  };
  
  // Resend verification code
  const resendVerificationCode = async (email) => {
    try {
      setLoading(true);
      await axios.post('/api/auth/resend-verification', { email });
      return true;
    } catch (err) {
      setError(err.response?.data?.error || 'Error al reenviar el código de verificación');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        error,
        isAuthenticated,
        register,
        login,
        logout,
        clearError,
        verifyEmail,
        resendVerificationCode
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
